import { useState, useEffect, ReactNode } from "react";
import { Spinner } from "@chakra-ui/react";
import { FiCheckCircle } from "react-icons/fi";
import DOMPurify from "dompurify";
import { getFinalScores } from "../../../api/final-scoring-api";
import type { SupplierEvaluationSummary, FinalScoringDetail } from "../../../types/scoring.type";
import { ScoringAccordion } from "../../ScoringAccordion";
import { cn } from "utils/helpers";

interface FinalSubmitConfirmationProps {
	evalId: number;
	rfxId: number;
	submittedAt?: string | null;
	suppliers: SupplierEvaluationSummary[];
}

const formatDate = (dateStr: string | null | undefined): string => {
	if (!dateStr) {
		return "";
	}
	const date = new Date(dateStr);
	return date.toLocaleDateString("en-AU", {
		day: "2-digit",
		month: "short",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});
};

const formatWeighted = (items: { score: number | null; weight: number }[]): string => {
	const valid = items.filter((i): i is { score: number; weight: number } => i.score !== null);
	if (valid.length === 0) return "—";
	const ws = valid.reduce((sum, i) => sum + i.score * (i.weight / 100), 0);
	return ws % 1 === 0 ? String(ws) : ws.toFixed(1);
};

const scoreDiff = (initial: number | string | null, final: number | string | null): ReactNode => {
	if (initial === null || final === null) {
		return "-";
	}
	const diffValue = parseFloat(final.toString()) - parseFloat(initial.toString());
	const diff = diffValue % 1 === 0 ? String(diffValue) : diffValue.toFixed(1);

	return <span className={cn(
		diffValue === 0 && "text-gray-300",
		diffValue > 0 && "text-green-600",
		diffValue < 0 && "text-red-600",
		"text-xs font-medium"
	)}>
		{diffValue > 0 ? `+${diff}` : diffValue === 0 ? "—" : String(diff)}
	</span>;
};

export const FinalSubmitConfirmation: React.FC<FinalSubmitConfirmationProps> = ({
	rfxId,
	evalId,
	suppliers,
	submittedAt,
}) => {
	const [details, setDetails] = useState<Map<number, FinalScoringDetail>>(new Map());
	const [isLoading, setIsLoading] = useState(true);

	useEffect(() => {
		if (suppliers.length === 0) {
			setIsLoading(false);
			return;
		}
		let cancelled = false;
		const load = async () => {
			const results = await Promise.allSettled(
				suppliers.map((s) => getFinalScores(rfxId, evalId, s.inviteId))
			);
			if (cancelled) {
				return;
			}
			const map = new Map<number, FinalScoringDetail>();
			results.forEach((r) => {
				if (r.status === "fulfilled") {
					map.set(r.value.inviteId, r.value);
				}
			});
			setDetails(map);
			setIsLoading(false);
		};
		load();
		return () => { cancelled = true; };
	}, [rfxId, evalId, suppliers]);

	return (
		<div className="space-y-4" data-testid="final-submit-confirmation">
			<div className="flex items-center gap-3 rounded-lg border border-green-200 bg-green-50 px-4 py-3">
				<FiCheckCircle size={20} className="shrink-0 text-green-600" aria-hidden="true" />
				<div>
					<p className="text-sm font-medium text-green-800">
						Your final scores have been submitted
					</p>
					{submittedAt && (
						<p className="text-xs text-green-600">
							Submitted on {formatDate(submittedAt)}
						</p>
					)}
					<p className="mt-1 text-xs text-green-700">
						Your final evaluation is complete. The RFx Lead will review all panel
						submissions and proceed to the next step.
					</p>
				</div>
			</div>

			<div>
				<h4 className="mb-3 text-sm font-medium text-gray-700">
					Your submitted final scores (read-only)
				</h4>

				{isLoading ? (
					<div className="flex items-center gap-2 py-6 text-sm text-gray-400">
						<Spinner size="sm" />
						Loading scores...
					</div>
				) : (
					<ScoringAccordion
						items={suppliers.map((s) => {
							const detail = details.get(s.inviteId);
							const criteria = detail?.criteria ?? [];
							const finalWeighted = formatWeighted(criteria.map((c) => ({ score: c.finalScore, weight: c.criterionWeight })));
							return { id: s.inviteId, logo: s.supplierLogo, name: s.supplierName, score: finalWeighted };
						})}
						rfxId={rfxId}
					>
						{(item) => {
							const detail = details.get(item.id);
							const criteria = detail?.criteria ?? [];
							const originalWeighted = formatWeighted(criteria.map((c) => ({ score: c.initialScore, weight: c.criterionWeight })));
							const finalWeighted = formatWeighted(criteria.map((c) => ({ score: c.finalScore, weight: c.criterionWeight })));

							return (
								<div className="overflow-x-auto">
									<table className="w-full table-fixed border-collapse text-sm">
										<colgroup>
											<col className="w-[220px]" />
											<col className="w-[60px]" />
											<col className="w-[70px]" />
											<col className="w-[80px]" />
											<col className="w-[80px]" />
											<col className="w-[80px]" />
											<col className="w-[50px]" />
											<col />
										</colgroup>
										<thead>
											<tr className="text-left text-xs text-gray-400">
												<th className="px-4 py-2 font-medium">Criterion</th>
												<th className="px-4 py-2 font-medium text-right">Weight</th>
												<th className="px-4 py-2 font-medium text-right">Initial</th>
												<th className="px-4 py-2 font-medium text-right">Weighted</th>
												<th className="px-4 py-2 font-medium text-right text-gray-600">Final</th>
												<th className="px-4 py-2 font-medium text-right text-gray-600">Weighted</th>
												<th className="px-4 py-2 font-medium text-right">+/&#x2212;</th>
												<th className="px-4 py-2 font-medium">Comment</th>
											</tr>
										</thead>
										<tbody>
											{criteria.map((c) => {
												const scoreDifferenceDisplay = scoreDiff(c.initialScore, c.finalScore);
												return (
													<tr
														key={c.rfxCriteriaId}
														className="border-t border-gray-50 align-top"
													>
														<td className="px-4 py-2 text-gray-700 break-words">
															{c.criterionName}
														</td>
														<td className="px-4 py-2 text-right text-gray-500 tabular-nums">
															{c.criterionWeight}%
														</td>
														<td className="px-4 py-2 text-right text-gray-400 tabular-nums">
															{c.initialScore !== null ? c.initialScore : "—"}
														</td>
														<td className="px-4 py-2 text-right text-gray-400 tabular-nums">
															{c.initialScore !== null ? formatWeighted([{ score: c.initialScore, weight: c.criterionWeight }]) : "—"}
														</td>
														<td className="px-4 py-2 text-right font-semibold tabular-nums text-gray-900 text-base">
															{c.finalScore !== null ? c.finalScore : "—"}
														</td>
														<td className="px-4 py-2 text-right font-semibold tabular-nums text-gray-900 text-base">
															{c.finalScore !== null ? formatWeighted([{ score: c.finalScore, weight: c.criterionWeight }]) : "—"}
														</td>
														<td className="px-4 py-2 text-right tabular-nums">
															{scoreDifferenceDisplay}
														</td>
														<td className="px-4 py-2 text-gray-500 text-xs">
															{c.finalComment ? (
																<div
																	className="prose prose-xs max-w-none [&>p]:m-0"
																	dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(c.finalComment) }}
																/>
															) : "—"}
														</td>
													</tr>
												);
											})}
										</tbody>
										<tfoot>
											<tr className="border-t border-gray-200">
												<td className="px-4 py-2 text-sm font-medium text-gray-900">Total</td>
												<td className="px-4 py-2" />
												<td className="px-4 py-2" />
												<td className="px-4 py-2 text-right text-gray-400 tabular-nums">
													{originalWeighted}
												</td>
												<td className="px-4 py-2" />
												<td className="px-4 py-2 text-right font-bold tabular-nums text-gray-900 text-base">
													{finalWeighted}
												</td>
												<td className="px-4 py-2 text-right">{scoreDiff(originalWeighted, finalWeighted)}</td>
												<td className="px-4 py-2" />
											</tr>
										</tfoot>
									</table>
								</div>
							);
						}}
					</ScoringAccordion>
				)}
			</div>
		</div>
	);
};
